/**
 * 痛みレベルに応じた文字色パレット
 * 0 (正常) → 100 (死) まで段階的に変化
 * グレー → ダークレッド → 黒へと変色
 */

export const painColorScale = {
  0: '#6b7280',   // gray-500 (正常 - 暗めのグレー)
  5: '#9ca3af',   // gray-400 (わずかな違和感)
  10: '#f87171',  // red-400 (軽い痛み)
  20: '#ef4444',  // red-500
  30: '#dc2626',  // red-600
  40: '#b91c1c',  // red-700
  50: '#991b1b',  // red-800
  60: '#7f1d1d',  // red-900
  70: '#8B0000',  // dark red (中程度の痛み)
  80: '#660000',  // darker red
  84: '#330000',  // very dark red (transition point)
  85: '#000000',  // black (same as 100)
  90: '#000000',  // black (same as 100)
  95: '#000000',  // black (same as 100)
  100: '#000000', // black (死)
} as const

/**
 * 痛みレベルから最適な色を取得
 * @param painLevel 0-100の痛みレベル
 * @returns HEXカラーコード
 */
export function getPainColor(painLevel: number): string {
  const clampedLevel = Math.max(0, Math.min(100, painLevel))

  // 定義されたレベルをキーとして取得
  const levels = Object.keys(painColorScale).map(Number).sort((a, b) => a - b)

  // 正確なレベルが存在する場合
  if (clampedLevel in painColorScale) {
    return painColorScale[clampedLevel as keyof typeof painColorScale]
  }

  // 補間して中間色を計算
  for (let i = 0; i < levels.length - 1; i++) {
    const lowerLevel = levels[i]!
    const upperLevel = levels[i + 1]!

    if (clampedLevel >= lowerLevel && clampedLevel <= upperLevel) {
      const lowerColor = painColorScale[lowerLevel as keyof typeof painColorScale]
      const upperColor = painColorScale[upperLevel as keyof typeof painColorScale]

      // 線形補間の比率を計算
      const ratio = (clampedLevel - lowerLevel) / (upperLevel - lowerLevel)

      return interpolateColor(lowerColor, upperColor, ratio)
    }
  }

  return painColorScale[100]
}

/**
 * 2つの色を線形補間
 */
function interpolateColor(color1: string, color2: string, ratio: number): string {
  const hex2rgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? {
      r: parseInt(result[1]!, 16),
      g: parseInt(result[2]!, 16),
      b: parseInt(result[3]!, 16)
    } : { r: 0, g: 0, b: 0 }
  }

  const rgb2hex = (r: number, g: number, b: number) => {
    return '#' + [r, g, b].map(x => {
      const hex = Math.round(x).toString(16)
      return hex.length === 1 ? '0' + hex : hex
    }).join('')
  }

  const c1 = hex2rgb(color1)
  const c2 = hex2rgb(color2)

  const r = c1.r + (c2.r - c1.r) * ratio
  const g = c1.g + (c2.g - c1.g) * ratio
  const b = c1.b + (c2.b - c1.b) * ratio

  return rgb2hex(r, g, b)
}

/**
 * 痛みレベルに応じたCSSクラス名を取得
 */
export function getPainClassName(painLevel: number): string {
  if (painLevel === 0) return 'text-gray-500'
  if (painLevel < 30) return 'text-red-500'
  if (painLevel < 60) return 'text-red-800'
  if (painLevel < 80) return 'text-[#8B0000]'
  if (painLevel < 95) return 'text-[#330000]'
  return 'text-black'
}

/**
 * 痛みレベルに応じたテキストエフェクト
 */
export function getPainTextEffect(painLevel: number): React.CSSProperties {
  const color = getPainColor(painLevel)

  if (painLevel >= 95) {
    // 極度の痛みレベル：黒い文字に強い赤い光
    return {
      color,
      textShadow: `0 0 10px rgba(220, 38, 38, 0.9), 0 0 20px rgba(185, 28, 28, 0.7)`
    }
  }

  if (painLevel >= 90) {
    // 高い痛みレベル：黒に近い文字に赤い光
    return {
      color,
      textShadow: `0 0 8px rgba(220, 38, 38, 0.8), 0 0 16px rgba(185, 28, 28, 0.6)`
    }
  }

  if (painLevel >= 80) {
    // 高い痛みレベル：暗い文字に赤い光
    return {
      color,
      textShadow: `0 0 6px rgba(220, 38, 38, 0.7), 0 0 12px rgba(185, 28, 28, 0.5)`
    }
  }

  if (painLevel >= 60) {
    // 中〜高痛みレベル：赤い光
    return {
      color,
      textShadow: `0 0 4px rgba(220, 38, 38, 0.6), 0 0 8px rgba(185, 28, 28, 0.4)`
    }
  }

  if (painLevel >= 40) {
    // 中程度の痛み：やや赤い光
    return {
      color,
      textShadow: `0 0 3px rgba(220, 38, 38, 0.5)`
    }
  }

  return { color }
}